/**
 * ZENVOYA — the base brand. Always present, never deleted.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every brand-dependent colour and asset both portals paint with, at its
 * Zenvoya value. Another brand folder beside this one (e.g. `../navi/`) only
 * lists what it CHANGES; anything it leaves out falls back to this file, and
 * deleting that folder brings this look back in full (see `../index.js`).
 *
 * Groups (read through `theme/tokens`, never imported directly by components):
 *   brand                   MUI primary / secondary ramp      → colors.brand
 *   sidebarChrome           sidebar collapse button ink       → colors.sidebarChrome
 *   sidebar                 both portals' sidebars            → colors.sidebar
 *   adminLogin              the CRM login page                → colors.adminLogin
 *   accent                  soft accent UI (chips, selected    → colors.accent
 *                           toggles, the acting-partner bar)
 *   servicingAccent         CRM servicing screens accent       → colors.servicingAccent
 *   pill                    a select styled as a pill (GST)    → colors.pill
 *   partnerConsoleAlpha     translucent console primary        → colors.partnerConsoleAlpha
 *   partnerLogin            the partner login, no partner code → partnerThemes.defaultLoginTheme
 *   partnerConsolePrimary   AntD primary in the partner console → colors.partnerConsole.primary
 *   partnerFallback         `--pa-brand-*` fallbacks          → partnerBrand
 *   partnerDefaultBranding  a partner with no saved branding  → brandTokens.FALLBACK_BRANDING
 */
import { ReactComponent as Wordmark } from 'assets/svg/zenvoya-logo-full.svg';
import logomark from 'assets/img/layout/zoya-logomark.png';
import { palette } from '../../palette';

const zenvoya = {
  id: 'zenvoya',
  name: 'Zenvoya',
  /** Wordmark (an SVG drawn in `currentColor`), the square mark, the tab icon. */
  Wordmark,
  logomark,
  favicon: null, // keep public/favicon.ico from index.html
  /**
   * Wordmark height on the CRM login page. The partner login sizes its wordmark
   * by WIDTH (150–200px), so a brand whose wordmark is less wide than Zenvoya's
   * sets a taller height here to come out the same size on both logins.
   */
  adminLoginLogoHeight: 32,

  colors: {
    brand: {
      primary: palette.zenvoya.primary,
      primaryLight: palette.zenvoya.primaryLight,
      primaryDark: palette.zenvoya.primaryDark,
      secondary: palette.zenvoya.secondary,
      secondaryLight: palette.zenvoya.secondaryLight,
      secondaryDark: palette.zenvoya.secondaryDark,
    },

    // Soft accent UI inside pages: selected toggles, accent chips, focus and
    // hover borders (e.g. the CRM's acting-partner bar).
    accent: {
      main: palette.tw.violet[700],
      tint: palette.tw.violet[50],
      fill: palette.tw.violet[100],
      border: palette.tw.violet[200],
      borderHover: palette.tw.violet[300],
      /** Text on `fill` — accent chips and in-page links. */
      chipText: palette.tw.violet[600],
      /** A second chip fill some screens use beside `fill`. */
      fillAlt: palette.tw.purple[100],
      /** Soft accent surfaces: a large decorative icon, a tinted note box. */
      iconSoft: palette.custom.violet[200],
      surface: palette.alpha.zenvoyaPrimaryLight[6],
      surfaceBorder: palette.alpha.zenvoyaPrimaryLight[18],
      /** An upload drop zone: on hover, and while a file is dragged over it. */
      dropHover: palette.custom.violet[20],
      dropActive: palette.custom.violet[50],
    },

    // The CRM's own accent on the shared servicing screens (and the request
    // panels of Booking Detail) — the older partner-demo violet.
    servicingAccent: {
      main: palette.zenvoya.legacyPartnerPrimary,
      light: palette.custom.indigo[40],
      tint: palette.alpha.zenvoyaLegacyPartnerPrimary[4],
      chipBg: palette.tw.violet[100],
      chipFg: palette.tw.violet[600],
      text: palette.tw.violet[700],
    },

    sidebarChrome: {
      icon: palette.custom.violet[360],
      iconHover: palette.custom.violet[220],
    },

    // The dark violet panel, identical in both portals.
    sidebar: {
      background: `linear-gradient(180deg, ${palette.custom.violet[850]} 0%, ${palette.custom.violet[890]} 100%)`,
      /** Ring around the API-status dot: the panel colour where the dots sit. */
      ring: palette.custom.violet[850],
      text: palette.custom.violet[330],
      textHover: palette.custom.violet[80],
      textActive: palette.custom.violet[51],
      iconActive: palette.custom.violet[290],
      accent: palette.zenvoya.primaryLight,
      activeBg: palette.alpha.zenvoyaPrimaryLight[18],
      hoverBg: palette.alpha.zenvoyaPrimaryLight[10],
      divider: palette.alpha.zenvoyaPrimaryLight[15],
      scrollThumb: palette.alpha.zenvoyaPrimaryLight[20],
      scrollThumbHover: palette.alpha.zenvoyaPrimaryLight[35],
      groupLabel: palette.custom.violet[500],
      footerText: palette.custom.violet[580],
      /** The new-chat glyph on hover. */
      actionHover: palette.white,
      /** The partner's name when it has no logo (partner sidebar). */
      brandText: palette.white,
      /** Edge between the panel and the page. */
      edge: 'none',
    },

    adminLogin: {
      border: palette.custom.violet[70],
      muted: palette.custom.violet[260],
      primary: palette.zenvoya.primary,
      primaryHover: palette.custom.violet[670],
      highlight: palette.zenvoya.lime,
      panelBg: palette.custom.violet[800],
      panelText: palette.custom.violet[260],
      panelHeading: palette.white,
      /** The 2px line between the panel and the form. */
      divider: palette.zenvoya.primary,
      cardText: palette.custom.violet[70],
      cardBg: palette.alpha.white[4],
      orb1: palette.alpha.zenvoyaPrimary[15],
      orb2: palette.alpha.zenvoyaLime[6],
      orb3: palette.alpha.zenvoyaSky[5],
      cardBorder: palette.alpha.customViolet260[12],
      cardHoverBg: palette.alpha.zenvoyaPrimary[12],
      cardHoverBorder: palette.alpha.customViolet260[25],
      iconBoxBg: palette.alpha.zenvoyaPrimary[25],
      iconBoxBorder: palette.alpha.customViolet260[15],
      badgeBorder: palette.alpha.customViolet260[10],
      focusRing: palette.alpha.zenvoyaPrimary[8],
      ghostHover: palette.alpha.zenvoyaPrimary[6],
      buttonShadow: palette.alpha.zenvoyaPrimary[30],
      buttonShadowHover: palette.alpha.zenvoyaPrimary[40],
      noteBg: palette.alpha.zenvoyaPrimary[3],
    },

    // #RRGGBB only: LoginPage appends a hex-alpha suffix (`${orbColor1}20`).
    partnerLogin: {
      panelBg: '#2D0066',
      panelAccent: '#6C00F6',
      panelAccentDeep: '#4A00A8',
      accentHighlight: '#A1F919',
      accentHighlightSoft: '#EEFFC9',
      accentHighlightMuted: '#D7FF84',
      softAccent: '#B07CFF',
      orbColor1: '#6C00F6',
      orbColor2: '#0077A8',
      orbColor3: '#A1F919',
      btnBg: '#4A00A8',
      btnHoverBg: '#6C00F6',
      inputFocusBorder: '#6C00F6',
      inputHoverBorder: '#B07CFF',
      headingColor: '#2D0066',
      tagColor: '#6C00F6',
      dividerColor: '#6C00F6',
      // Text and icons on the panel. A partner's own login theme does not carry
      // this, and LoginPage falls back to these same values for it.
      panelInk: {
        heroText: palette.white,
        panelText: palette.alpha.white[50],
        stepTitle: palette.white,
        stepText: palette.alpha.white[45],
        stepIcon: palette.white,
        footerText: palette.alpha.white[20],
        poweredText: palette.white,
      },
    },

    partnerConsolePrimary: palette.zenvoya.legacyPartnerPrimary,

    // Translucent partner-console primary (antdTheme: selected menu, focus ring).
    partnerConsoleAlpha: {
      8: palette.alpha.zenvoyaLegacyPartnerPrimary[8],
      20: palette.alpha.zenvoyaLegacyPartnerPrimary[20],
    },

    // A select styled as a pill (partner GST page's branch picker).
    pill: {
      bg: palette.tw.indigo[100],
      text: palette.tw.indigo[800],
      icon: palette.tw.indigo[600],
      divider: palette.tw.indigo[400],
      tagBg: palette.tw.indigo[200],
      borderHover: palette.tw.indigo[200],
      ring: palette.tw.indigo[300],
      borderFocus: palette.tw.indigo[500],
    },

    partnerFallback: {
      primary: palette.zenvoya.legacyPartnerPrimary,
      primaryLight: palette.tw.violet[100],
      secondary: palette.zenvoya.lime,
    },

    partnerDefaultBranding: {
      primaryColor: palette.zenvoya.primary,
      secondaryColor: palette.zenvoya.lime,
      accentColor: palette.zenvoya.sky,
    },
  },
};

export default zenvoya;
